import type { ResultPrefill } from "stores/useRecipeRunStore";
import type {
  MaterializeRequest,
  RecipeDetail,
  RecipeLens,
  RecipeParamForm,
} from "./recipesApi";
import { cannotRunText } from "./recipesApi";

/** A parameter's text as typed. `undefined` is untouched — the recipe's own
 *  default applies and nothing is sent; `""` is cleared on purpose. */
export type ArgDraft = Record<string, string | undefined>;

export interface RunDraft {
  args: ArgDraft;
  /** Slot name → the chosen pick, as `JSON.stringify(choice.value)`. */
  picks: Record<string, string | undefined>;
  lens: RecipeLens;
  tableName: string;
}

/** What the overlay adds to the door's request: the model and the drop point
 *  come from where it was opened. */
export type RunBody = Omit<MaterializeRequest, "model_id" | "at">;

export type BuiltRun =
  { ok: true; body: RunBody } | { ok: false; problems: Record<string, string> };

const INTEGER_KINDS = new Set(["I32", "I64", "U32", "U64"]);
const FLOAT_KINDS = new Set(["F32", "F64"]);

/** What the field asks for, in the reader's words. */
export function kindHint(param: RecipeParamForm): string {
  const { kind } = param;
  if (INTEGER_KINDS.has(kind)) return "whole number";
  if (FLOAT_KINDS.has(kind)) return "number";
  switch (kind) {
    case "Bool":
      return "true or false";
    case "Decimal": {
      const scale = param.params.decimal?.scale ?? 2;
      return `decimal, e.g. ${(0).toFixed(scale).replace(/^0/, "12")}`;
    }
    case "Date":
      return "date, YYYY-MM-DD";
    case "TimestampUTCSeconds":
    case "TimestampUTCMicroseconds":
    case "TimestampUTCNanoseconds":
      return "timestamp, RFC 3339";
    case "Uuid":
      return "UUID";
    case "ListString":
      return "comma-separated values";
    case "Categorical":
      return "category";
    default:
      return "text";
  }
}

/** The run-side value for what was typed, or why it cannot be one. Only the
 *  JSON shape is decided here — the server validates the value (R5); this
 *  keeps a whole number from travelling as a string and nothing more. */
export function parseArg(
  param: RecipeParamForm,
  text: string,
): { value: unknown } | { error: string } {
  const trimmed = text.trim();
  if (param.choices.source === "literal") {
    try {
      return { value: JSON.parse(text) };
    } catch {
      return { error: "pick one of the listed values" };
    }
  }
  if (INTEGER_KINDS.has(param.kind)) {
    if (!/^-?\d+$/.test(trimmed)) return { error: "a whole number" };
    const n = Number(trimmed);
    if (!Number.isSafeInteger(n)) return { error: "too large a number" };
    return { value: n };
  }
  if (FLOAT_KINDS.has(param.kind)) {
    const n = Number(trimmed);
    if (trimmed === "" || !Number.isFinite(n)) return { error: "a number" };
    return { value: n };
  }
  switch (param.kind) {
    case "Bool":
      if (trimmed === "true") return { value: true };
      if (trimmed === "false") return { value: false };
      return { error: "true or false" };
    case "Decimal":
      if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return { error: "a decimal" };
      return { value: trimmed };
    case "ListString":
      return {
        value: text
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
      };
    default:
      return { value: text };
  }
}

const sameJson = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

const isAbsentFilter = (pick: unknown): boolean =>
  typeof pick === "object" &&
  pick !== null &&
  (pick as { kind?: string }).kind === "filter" &&
  (pick as { present?: boolean }).present === false;

/** The draft a reopened result starts from: its stored arguments and picks
 *  spelled the way the controls hold them, so an untouched form rebuilds the
 *  same identity and replaces the result in place (R15). */
export function draftFromResult(
  detail: RecipeDetail,
  result: ResultPrefill,
): RunDraft {
  const args: ArgDraft = {};
  for (const param of detail.params) {
    if (isAbsentFilter(result.picks[param.name])) {
      args[param.name] = "";
      continue;
    }
    const value = result.args[param.name];
    if (value === null || value === undefined) continue;
    args[param.name] =
      param.choices.source === "literal"
        ? JSON.stringify(value)
        : Array.isArray(value)
          ? value.join(", ")
          : String(value);
  }
  const picks: Record<string, string> = {};
  for (const slot of detail.slots) {
    if (slot.kind === "optional_filter") continue;
    const pick = result.picks[slot.name];
    if (pick !== undefined) picks[slot.name] = JSON.stringify(pick);
  }
  return { args, picks, lens: result.lens, tableName: result.tableName };
}

/** The request body from the draft: only what differs from the recipe's own
 *  defaults travels, so a run with nothing changed lands on the same result as
 *  one that spelled the defaults out (R15). A cleared optional-filter argument
 *  becomes an explicit `present: false` pick — omitting it would leave the
 *  placeholder unbound instead of dropping the predicate. */
export function buildRunRequest(
  detail: RecipeDetail,
  draft: RunDraft,
): BuiltRun {
  const problems: Record<string, string> = {};
  if (detail.cannot_run) {
    problems.recipe = `cannot run: ${cannotRunText(detail.cannot_run)}`;
  }
  const tableName = draft.tableName.trim();
  if (tableName === "") problems.table_name = "the result needs a name";

  const filterSlots = new Map(
    detail.slots
      .filter((slot) => slot.kind === "optional_filter")
      .map((slot) => [slot.name, slot] as const),
  );
  const args: Record<string, unknown> = {};
  const picks: Record<string, unknown> = {};

  for (const param of detail.params) {
    const text = draft.args[param.name];
    if (text === undefined) {
      if (param.required && param.default === null) {
        problems[param.name] = "required";
      }
      continue;
    }
    if (text.trim() === "") {
      if (filterSlots.has(param.name)) {
        picks[param.name] = { kind: "filter", present: false };
      } else if (param.required && param.default === null) {
        problems[param.name] = "required";
      }
      continue;
    }
    const parsed = parseArg(param, text);
    if ("error" in parsed) {
      problems[param.name] = parsed.error;
      continue;
    }
    if (!sameJson(parsed.value, param.default)) args[param.name] = parsed.value;
  }

  for (const slot of detail.slots) {
    if (slot.kind === "optional_filter") continue;
    const chosen = draft.picks[slot.name];
    if (chosen === undefined || chosen === JSON.stringify(slot.default)) {
      continue;
    }
    try {
      picks[slot.name] = JSON.parse(chosen);
    } catch {
      problems[slot.name] = "pick one of the listed values";
    }
  }

  if (Object.keys(problems).length > 0) return { ok: false, problems };
  const body: RunBody = { table_name: tableName };
  if (Object.keys(args).length > 0) body.args = args;
  if (Object.keys(picks).length > 0) body.picks = picks;
  if (detail.lens_choice && draft.lens !== detail.lens) body.lens = draft.lens;
  // Nothing else may ask for a lens: the door decides it, and an input with
  // no release makes `released` unrunnable (R18).
  return { ok: true, body };
}
